import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { products, productCategories } from "@/mocks/products";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredProducts = useMemo(() => {
    let result = products;
    if (activeCategory !== "all") {
      result = result.filter((p) => p.categorySlug === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q));
    }
    return result;
  }, [activeCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    products.forEach((p) => {
      counts[p.categorySlug] = (counts[p.categorySlug] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-10 md:pb-16 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold uppercase tracking-wider mb-3">
                  {t("navProducts")}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                  {t("productsTitle")}
                </h1>
                <p className="text-foreground-600 text-sm md:text-base max-w-xl">
                  {t("productsSubtitle")}
                </p>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap inline-flex items-center gap-2"
              >
                {t("ctaBuyNow")}
                <i className="ri-external-link-line" />
              </a>
            </div>

            {/* Search */}
            <div className="relative max-w-md mb-6">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("productsSearchPlaceholder")}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-300"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-200/70 text-foreground-700 hover:bg-background-300/60"
                }`}
              >
                {t("allCategories")} ({products.length})
              </button>
              {productCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200/70 text-foreground-700 hover:bg-background-300/60"
                  }`}
                >
                  {cat.name} ({categoryCounts[cat.slug] || 0})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-10 md:py-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <i className="ri-search-line text-4xl text-foreground-300 mb-3" />
                <p className="text-foreground-500">{t("noProductsFound")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="group rounded-xl border border-background-200/70 bg-background-100 overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all flex flex-col"
                  >
                    <div className="relative aspect-square overflow-hidden bg-background-200/50">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://readdy.ai/api/search-image?query=placeholder%20product%20image&width=400&height=400&seq=placeholder&orientation=squarish";
                        }}
                      />
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 rounded-md bg-background-50/90 backdrop-blur-sm text-xs font-medium text-foreground-700 border border-background-200/70">
                          {product.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4 flex flex-col flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 leading-snug mb-2 line-clamp-2 flex-1">
                        {product.name}
                      </h3>
                      <p className="text-sm font-bold text-primary-600 mb-3">
                        {product.price}
                      </p>
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block w-full text-center px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        {t("buyNow")}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20 bg-primary-500/10">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t("finalCtaTitle")}
            </h2>
            <p className="text-foreground-600 mb-6 max-w-xl mx-auto">
              {t("finalCtaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors shadow-sm whitespace-nowrap"
              >
                {t("heroCtaPrimary")}
              </a>
              <Link
                to="/guides"
                className="px-6 py-3 rounded-lg border-2 border-primary-500 text-primary-700 font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                {t("heroCtaSecondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}